import {
  getAccessToken,
  setAccessToken,
  emitForcedLogout,
} from '@/features/auth/tokenStore';
import { readImpersonationClaims } from '@/features/auth/impersonation';

/**
 * The browser's ONLY backend is same-origin. On beta.raadtelematics.com the SPA
 * owns the whole origin and Caddy proxies /auth/* and /api/* straight through to
 * raad-api with NO prefix stripping — so the client calls bare relative paths
 * (/auth/login, /api/me) and the API base is empty. Relative paths only — no
 * absolute api.raadtelematics.com, no Traccar URL ever reaches this bundle.
 * Cookies are genuinely first-party same-origin here, so
 * `credentials: 'same-origin'` carries the httpOnly refresh cookie on the
 * /auth/* paths and nothing custom is needed elsewhere.
 */
export const API_BASE = '';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string | null,
    message?: string,
  ) {
    super(message ?? code ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

/** Thrown when the session cannot be recovered — the caller routes to /login. */
export class AuthError extends Error {
  constructor(message = 'session_expired') {
    super(message);
    this.name = 'AuthError';
  }
}

function url(path: string): string {
  return `${API_BASE}${path}`;
}

async function authedFetch(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body != null && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const token = getAccessToken();
  if (token) headers.set('authorization', `Bearer ${token}`);
  return fetch(url(path), { ...init, headers, credentials: 'same-origin' });
}

// ── Single-flight refresh ──────────────────────────────────────────────────
// LOAD-BEARING: raad-api treats two concurrent refreshes of one token as reuse
// and burns the whole token family, force-logging the user out. So a burst of
// 401s must collapse to EXACTLY ONE POST /auth/refresh; every other caller
// awaits that same promise. Removing this dedupe reintroduces the spurious
// logout that AC-11 guards against.
let inFlightRefresh: Promise<string> | null = null;

async function runRefresh(): Promise<string> {
  // The httpOnly refresh cookie (scoped to /auth by raad-api on beta.) rides
  // along automatically. No token in the body — raad-api reads it from the
  // cookie (E1).
  const res = await fetch(url('/auth/refresh'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  });
  if (!res.ok) throw new AuthError('refresh_failed');
  const data = (await res.json()) as { accessToken?: string };
  if (!data.accessToken) throw new AuthError('refresh_failed');
  setAccessToken(data.accessToken);
  return data.accessToken;
}

export function refreshAccessToken(): Promise<string> {
  if (!inFlightRefresh) {
    inFlightRefresh = runRefresh().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

/**
 * Authorized request to raad-api. On a 401 it runs a single-flight refresh and
 * replays the request exactly once. A failed refresh is a terminal logout
 * (expiry / revocation / reuse-burn are indistinguishable by design — we never
 * try to tell them apart and never loop): wipe state, notify, surface AuthError.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
  opts: { retryOnAuth?: boolean } = {},
): Promise<Response> {
  const retryOnAuth = opts.retryOnAuth ?? true;
  const res = await authedFetch(path, init);
  if (res.status !== 401 || !retryOnAuth) return res;

  // 🔴 AN IMPERSONATION SESSION NEVER REFRESHES (slice 17c). It has NO refresh
  // token by design — the admin portal mints the read-only access token and
  // delivers it in the URL fragment; no refresh cookie is ever set for it. When
  // its ~10-minute token expires, a 401 must end the session cleanly, NOT run
  // `POST /auth/refresh` against whatever cookie happens to sit on the app.
  // origin: that cookie belongs to a DIFFERENT principal (a staff member's own
  // session, say), so refreshing would silently swap the session to another
  // tenant while the screen still showed the impersonated one — the wrong-tenant
  // display this product guards hardest against. Forced logout drops to /login
  // (a clear "session ended"), which is the correct end-of-impersonation state.
  if (readImpersonationClaims(getAccessToken()).readOnly) {
    emitForcedLogout();
    throw new AuthError('impersonation_ended');
  }

  try {
    await refreshAccessToken();
  } catch {
    emitForcedLogout();
    throw new AuthError('session_expired');
  }
  // Replay once. No further retry — a second 401 falls through to the caller.
  return authedFetch(path, init);
}

/** apiFetch + JSON, throwing ApiError on non-2xx. */
export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, init);
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const code =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : null;
    throw new ApiError(res.status, code);
  }
  return body as T;
}
